"use client"

import { InitialConfigType, LexicalComposer } from "@lexical/react/LexicalComposer"
import { useLexicalComposerContext } from "@lexical/react/LexicalComposerContext"
import { $generateHtmlFromNodes } from "@lexical/html"
import { useEffect, useState } from "react"
import { SerializedEditorState } from "lexical"

import { editorTheme } from "@/components/blocks/editor-00/themes/editor-theme"
import { nodes } from "@/components/blocks/editor-00/nodes"
import { cn } from "@/lib/utils"

// HTML Generator component that runs inside LexicalComposer context
function HtmlGenerator({
  onHtmlGenerated
}: {
  onHtmlGenerated: (html: string) => void
}) {
  const [editor] = useLexicalComposerContext();

  useEffect(() => {
    // Generate HTML from current editor state
    const generateHtml = () => {
      editor.read(() => {
        try {
          // Use Lexical's built-in HTML generation for SEO-friendly output
          const htmlString = $generateHtmlFromNodes(editor, null);
          onHtmlGenerated(htmlString);
        } catch (error) {
          console.error("Error generating HTML:", error);
          onHtmlGenerated("");
        }
      });
    };

    // Generate HTML immediately
    generateHtml();

    // Listen for editor state changes to regenerate HTML
    const unregister = editor.registerUpdateListener(() => {
      generateHtml();
    });

    return unregister;
  }, [editor, onHtmlGenerated]);

  return null; // This component doesn't render anything
}

export interface RichContentRendererProps {
  editorSerializedState?: SerializedEditorState | string | null
  /** Applied to the outer wrapper. */
  className?: string
  /** Applied to the inner element that holds the generated HTML — use this to size, clamp or restyle the content itself. */
  contentClassName?: string
}

export function RichContentRenderer({
  editorSerializedState,
  className = "",
  contentClassName,
}: RichContentRendererProps) {
  const [generatedHtml, setGeneratedHtml] = useState<string>("");

  // Callers pass raw DB columns here, which may hold legacy plain text or a hollow
  // editor state such as the literal string `""`. Both survive a truthiness check but
  // throw inside LexicalComposer when it dereferences `.root`, taking the host tree
  // down with them — so require a real Lexical state with at least one child.
  const hasContent = (() => {
    if (!editorSerializedState) return false;

    if (typeof editorSerializedState === 'string') {
      if (editorSerializedState.trim() === '') return false;

      try {
        const parsed = JSON.parse(editorSerializedState);
        return Boolean(parsed?.root?.children?.length);
      } catch {
        return false;
      }
    }

    return Boolean((editorSerializedState as SerializedEditorState)?.root?.children?.length);
  })();

  // Return null for empty content (SEO-friendly)
  if (!hasContent) {
    return null;
  }

  // Preview config for Lexical composer
  const previewConfig: InitialConfigType = {
    namespace: "RichContentRenderer",
    theme: editorTheme,
    nodes,
    editable: false,
    onError: (error: Error) => {
      console.error("SEO content renderer error:", error);
    },
  };

  // Prepare the initial configuration
  const initialConfig: any = { ...previewConfig };

  try {
    if (typeof editorSerializedState === 'string') {
      // If it's already a JSON string, use it directly
      initialConfig.editorState = editorSerializedState;
    } else {
      // If it's an object, stringify it
      initialConfig.editorState = JSON.stringify(editorSerializedState || {});
    }
  } catch (error) {
    console.error("Error preparing editor state:", error);
    return null; // Return null on error for SEO-friendly behavior
  }

  return (
    <div className={className}>
      <LexicalComposer initialConfig={initialConfig}>
        {/* HTML Generator - generates SEO-friendly HTML */}
        <HtmlGenerator onHtmlGenerated={setGeneratedHtml} />

        {/* Render the generated HTML with prose styling */}
        {generatedHtml && (
          <div
            className={cn("prose prose-sm max-w-none prose-headings:scroll-m-20 prose-headings:tracking-tight prose-h1:text-4xl prose-h1:font-extrabold prose-h2:text-3xl prose-h2:font-semibold prose-h2:border-b prose-h2:pb-2 prose-h3:text-2xl prose-h3:font-semibold prose-h4:text-xl prose-h4:font-semibold prose-h5:text-lg prose-h5:font-semibold prose-h6:text-base prose-h6:font-semibold prose-p:leading-7 prose-p:[&:not(:first-child)]:mt-6 prose-blockquote:mt-6 prose-blockquote:border-l-2 prose-blockquote:pl-6 prose-blockquote:italic prose-ul:my-6 prose-ul:ml-6 prose-ul:[&>li]:mt-2 prose-ol:my-6 prose-ol:ml-6 prose-ol:list-decimal prose-ol:[&>li]:mt-2 prose-li:mx-8 prose-a:text-blue-600 hover:prose-a:underline prose-strong:font-bold prose-em:italic prose-code:bg-gray-100 prose-code:p-1 prose-code:rounded-md prose-pre:bg-muted prose-pre:p-4 prose-pre:rounded-lg prose-pre:overflow-x-auto", contentClassName)}
            dangerouslySetInnerHTML={{ __html: generatedHtml }}
          />
        )}
      </LexicalComposer>
    </div>
  );
}
